import * as React from 'react';

import { StockAvailability } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';
import { CornerTag } from '@components/Tags/CornerTag';
import {
  getSupportedAttributes,
  getSupportedCornerAttributes,
} from '@components/Tags/model';
import { getRenderableAttributes } from '@components/Tags/utils';
import { useIsDualPrice } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPrice';
import { usePriceWithVoucher } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/hooks/usePriceWithVoucher';
import { getVariantName } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { VariantDisplayTypes } from '@containers/ProductListing/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dataLabelsProp } from '@helpers/utils';

import { IVariantProps } from '../../../../model';
import { trackVariantClick } from '../../tracking';
import {
  VariantListItem,
  VariantLink,
  PriceLabelCurrency,
  Name,
} from '../../variantsStyled';
import { Unavailable } from '../Unavailable';

import { Variant, PriceLabelWrapper } from './styled';
import { trackTagClick } from './tracking';

export const VariantTile: React.FC<IVariantProps> = ({
  item,
  isSelected,
  unit,
  onClick,
}) => {
  const { getTimeFromInit } = useTrackingContext();
  const supportedCornerAttributes = getSupportedCornerAttributes();
  const supportedAttributes = getSupportedAttributes();

  const { isDualPrice } = useIsDualPrice();
  const isOnStock =
    item.availability.stockAvailability !== StockAvailability.OutOfStock;

  const price = usePriceWithVoucher(item);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(item, e);
    const attributes = Object.keys(
      getRenderableAttributes({
        attributes: item.attributes,
        supportedAttributes: supportedCornerAttributes,
      })
    );
    trackVariantClick({
      timing: getTimeFromInit(),
      type: VariantDisplayTypes.Tiles,
      variantName: getVariantName(item) ?? '',
      promoLabel: isOnStock ? attributes?.[0] : undefined,
    });
  };

  const cornerTagProps = {
    onClick: (attr) => trackTagClick(attr, getTimeFromInit()),
    attributes: item.attributes,
    stockAvailability: item.stockAvailability.code ?? '',
  };

  return (
    <VariantListItem minWidth="0px">
      <ErrorBoundary>
        <VariantLink
          onClick={handleClick}
          to={item.url ?? ''}
          replace={true}
          id={`pd-variant-${item.webId}`}
          data-testid={`pd-variant-${item.webId}`}
          className={isSelected ? 'pd-variant-selected' : ''}
          {...dataLabelsProp({
            attributes: item.attributes,
            supportedAttributes,
          })}
        >
          <Variant
            isOnStock={isOnStock}
            isDualPrice={isDualPrice}
            isSelected={isSelected}
          >
            <CornerTag {...cornerTagProps} />

            <Name isOnStock={isOnStock} className="pd-variant-label">
              {`${item.parameters?.package || ''}\u00A0${unit || ''}`}
            </Name>

            {isOnStock && (
              <PriceLabel
                dataTestId="price-variant"
                price={{
                  value: price.value ?? 0,
                  currency: price.currency ?? undefined,
                }}
                wrapperComponent={(additionalProps) => (
                  <PriceLabelWrapper {...additionalProps} />
                )}
                currencyComponent={PriceLabelCurrency}
              />
            )}

            {!isOnStock && <Unavailable />}
          </Variant>
        </VariantLink>
      </ErrorBoundary>
    </VariantListItem>
  );
};
